import { useQuery } from "@apollo/client";
import { Delete } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { Formik, Form } from "formik";
import _ from "lodash";
import { useSnackbar } from "notistack";
import { ReactElement, useState } from "react";
import * as Yup from "yup";

import {
  CreatePreviewMessageContactMutation,
  CreatePreviewMessageContactMutationVariables,
  DeletePreviewMessageContactMutation,
  DeletePreviewMessageContactMutationVariables,
  ListPreviewMessageContactsQuery,
  ListPreviewMessageContactsQueryVariables,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import Table, { TableBox } from "~frontend/components/Table";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import {
  CREATE_PREVIEW_MESSAGE_CONTACT,
  DELETE_PREVIEW_MESSAGE_CONTACT,
  LIST_PREVIEW_MESSAGE_CONTACTS,
} from "~frontend/graphql/previewMessages";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import {
  phoneNumberIsValid,
  prettyPhoneNumber,
} from "~frontend/util/phoneNumber";
import useMutation from "~frontend/util/useMutation";

function AddPreviewTextNumberModal({
  onClose,
  subaccountId,
  open,
}: {
  onClose: () => void;
  subaccountId: string;
  open: boolean;
}): ReactElement {
  const [addPreviewMessageContact] = useMutation<
    CreatePreviewMessageContactMutation,
    CreatePreviewMessageContactMutationVariables
  >(CREATE_PREVIEW_MESSAGE_CONTACT, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: LIST_PREVIEW_MESSAGE_CONTACTS,
        variables: {
          input: {
            subaccountId,
          },
        },
      },
    ],
  });

  const getErrorMessage = useErrorMessageFormatter();
  const snackbar = useSnackbar();

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>New Preview Text Number</DialogTitle>
      <Formik
        initialValues={{
          [submitErrorField]: null,
          name: "",
          phoneNumber: "",
        }}
        validationSchema={Yup.object({
          name: Yup.string().required("Name is required"),
          phoneNumber: Yup.string()
            .required("Phone number is required")
            .test({
              name: "validPhoneNumber",
              message: "Phone number is invalid",
              test: pn => !pn || phoneNumberIsValid(pn),
            }),
        })}
        onSubmit={async (values, formik) => {
          const { errors } = await addPreviewMessageContact({
            variables: {
              input: {
                subaccountId,
                name: values.name,
                phoneNumber: values.phoneNumber,
              },
            },
          });

          if (errors) {
            formik.setErrors({
              [submitErrorField]: getErrorMessage(errors),
            });
          } else {
            snackbar.enqueueSnackbar("Preview Text Number added", {
              variant: "success",
            });

            onClose();
            formik.resetForm();
          }
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <DialogContent sx={{ minWidth: "400px" }}>
              <FormikErrorBanner />
              <FormikTextField name="name" label="Display Name" fullWidth />
              <FormikTextField
                name="phoneNumber"
                label="Phone Number"
                fullWidth
              />
            </DialogContent>

            <DialogActions>
              <Button
                disabled={isSubmitting}
                onClick={onClose}
                color="tertiary"
                variant="contained"
              >
                Cancel
              </Button>

              <FormikSubmitButton
                color="primary"
                variant="contained"
                submitText="Add"
                loadingText="Adding..."
              />
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}

export default function SettingsPreviewContacts(): ReactElement {
  const { subaccount } = useSubaccount()!;
  const [addPreviewTextNumberOpen, setAddPreviewTextNumberOpen] =
    useState(false);

  const { data, loading, error } = useQuery<
    ListPreviewMessageContactsQuery,
    ListPreviewMessageContactsQueryVariables
  >(LIST_PREVIEW_MESSAGE_CONTACTS, {
    variables: {
      input: {
        subaccountId: subaccount.id,
      },
    },
  });

  const [deletePreviewMessageContact] = useMutation<
    DeletePreviewMessageContactMutation,
    DeletePreviewMessageContactMutationVariables
  >(DELETE_PREVIEW_MESSAGE_CONTACT, {
    onErrorSnackbarMessage: "Failed to delete preview text number",
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: LIST_PREVIEW_MESSAGE_CONTACTS,
        variables: {
          input: {
            subaccountId: subaccount.id,
          },
        },
      },
    ],
  });

  return (
    <Box sx={{ mt: 2 }}>
      <AsyncWrapper loading={loading} errors={error}>
        <Typography variant="display0Bold" sx={{ mb: 2 }}>
          Preview Text Numbers
        </Typography>
        <Typography variant="h2" sx={{ mb: 3 }}>
          Add numbers that you would like to send preview texts to here. When
          launching a campaign, you will have the option to send a sample of
          your texts to the numbers entered below.
        </Typography>

        <TableBox sx={{ backgroundColor: palette.lightestGray }}>
          <Table
            rows={_.sortBy(data?.previewMessageContacts ?? [], "name")}
            columns={[
              {
                key: "name",
                title: "Name",
                renderValue: row => <span>{row.name}</span>,
              },
              {
                key: "phone",
                title: "Phone Number",
                align: "left",
                renderValue: row => (
                  <span>{prettyPhoneNumber(row.phoneNumber)}</span>
                ),
              },
            ]}
            rowActions={[
              {
                id: "delete",
                icon: <Delete />,
                confirm: row => ({
                  title: "Delete Preview Text Number",
                  description: (
                    <span>
                      Are you sure you want to delete {row.name} -{" "}
                      {prettyPhoneNumber(row.phoneNumber)}?
                    </span>
                  ),
                  confirmationText: "Delete",
                  cancellationText: "Cancel",
                }),
                label: "Delete",
                onClick: async row => {
                  await deletePreviewMessageContact({
                    variables: { input: { previewMessageContactId: row.id } },
                  });
                },
              },
            ]}
            rowActionsLabel="Delete"
            emptyState={<>None found</>}
            noFooter
          />
          <FlexBox justifyContent="end" sx={{ mt: 2 }}>
            <Button
              onClick={() => setAddPreviewTextNumberOpen(true)}
              variant="contained"
              color="tertiary"
              sx={{ mb: 1 }}
            >
              <Typography variant="label2Caps" sx={{ letterSpacing: "0.48px" }}>
                + Add Preview Text Number
              </Typography>
            </Button>
          </FlexBox>
          <AddPreviewTextNumberModal
            open={addPreviewTextNumberOpen}
            onClose={() => setAddPreviewTextNumberOpen(false)}
            subaccountId={subaccount.id}
          />
        </TableBox>
      </AsyncWrapper>
    </Box>
  );
}
